import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Prontuarios from '../../services/Prontuarios';
import { SweetAlert } from '../sweetalert';
import { DEFAULT_ATTENDANCE_DESCRIPTION } from '../../utilities/attendance';

// Lançar um atendimento no prontuário ao salvar o agendamento (status marcado com "Lançar atendimento"):
// ask() pergunta; open() abre o modal do atendimento, e o Salvar dele grava o agendamento e, depois, o atendimento
export function useAgendamentoAttendance() {
  const form = useForm();
  const [question, setQuestion] = useState({ show: false, prontuario: '' });
  const [modal, setModal] = useState({ show: false, prontuario: '' });
  const [saving, setSaving] = useState(false);
  const answerRef = useRef(null);
  const flow = useRef(null); // { prontuarioId, save, resolve, agendamentoSaved }
  const draftProntuarioId = useRef(null);

  // Devolve 'yes', 'no' ou null (fechou a pergunta: volta ao agendamento sem salvar)
  function ask(prontuario) {
    setQuestion({ show: true, prontuario });
    return new Promise((resolve) => { answerRef.current = resolve; });
  }

  function answer(value) {
    setQuestion((current) => ({ ...current, show: false }));
    answerRef.current?.(value);
    answerRef.current = null;
  }

  // Devolve { saved, launched, prontuario }: saved = o agendamento foi gravado; launched = o atendimento também
  function open({ prontuarioId, prontuario, tecnicoId, tecnico, date, save }) {
    // Reaberto para o mesmo prontuário (ex.: o agendamento foi recusado), o texto digitado continua lá
    if (draftProntuarioId.current !== prontuarioId) {
      form.reset({ titulo_atendimento: '', descricao_atendimento: DEFAULT_ATTENDANCE_DESCRIPTION });
      draftProntuarioId.current = prontuarioId;
    }

    // Doutor e data vêm sempre do agendamento
    form.setValue('tecnico_atendimento_id', tecnicoId ?? '');
    form.setValue('tecnico_atendimento', tecnico ?? '');
    form.setValue('data_atendimento', date ?? '');

    setModal({ show: true, prontuario });
    setTimeout(() => form.setFocus('descricao_atendimento'), 500);

    return new Promise((resolve) => {
      flow.current = { prontuarioId, save, resolve, agendamentoSaved: false };
    });
  }

  function finish(result) {
    const current = flow.current;
    flow.current = null;
    setModal((state) => ({ ...state, show: false }));
    if (result.saved) draftProntuarioId.current = null;
    current?.resolve(result);
  }

  async function submit() {
    const current = flow.current;
    if (!current || saving) return;

    const values = form.getValues();

    if (!values.tecnico_atendimento_id) {
      SweetAlert.error('Doutor é um campo obrigatório', 'Atendimento');
      return;
    }

    if (!values.data_atendimento) {
      SweetAlert.error('Data do atendimento é um campo obrigatório', 'Atendimento');
      return;
    }

    setSaving(true);
    try {
      // O agendamento primeiro: se for recusado, nada é lançado e o modal continua aberto
      if (!current.agendamentoSaved) {
        if (!(await current.save())) return;
        current.agendamentoSaved = true;
      }

      try {
        const { data } = await Prontuarios.createAtendimento(current.prontuarioId, {
          tecnico_atendimento_id: values.tecnico_atendimento_id,
          data_atendimento: values.data_atendimento,
          titulo: values.titulo_atendimento ?? '',
          descricao: values.descricao_atendimento ?? '',
        });
        finish({ saved: true, launched: true, prontuario: data });
      } catch (error) {
        // O próximo Salvar tenta só o atendimento; o X fecha com o agendamento salvo, sem o atendimento
        const message = error?.response?.data?.message ?? error?.message;
        SweetAlert.error(`O agendamento foi salvo, mas o atendimento não foi lançado. ${message ?? ''} Clique em Salvar para tentar de novo.`, 'Atendimento');
      }
    } finally {
      setSaving(false);
    }
  }

  function close() {
    if (saving) return;
    finish({ saved: Boolean(flow.current?.agendamentoSaved), launched: false });
  }

  // O modal do agendamento fechou: o texto guardado não vale mais
  function clear() {
    draftProntuarioId.current = null;
  }

  return { form, question, modal, saving, ask, answer, open, submit, close, clear };
}
